import CloseOutlined from "@mui/icons-material/CloseOutlined";
import SearchOffOutlined from "@mui/icons-material/SearchOffOutlined";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isAxiosError } from "axios";
import { useEffect, useId, useState } from "react";
import { toast } from "react-toastify";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { listCompactSx } from "@/styles/sx/compact";
import { useSaleActions, useSaleDocumentDetail } from "../api/useReassistSale";
import { type SaleFile } from "../types";
import {
  displayName,
  formatFileLabel,
  formatPriceWithYen,
} from "../utils/format";
import {
  ReassistButton,
  reassistButtonSx,
} from "@/features/reassistRequests/components/ReassistButton";
import { LoadFailed } from "@/features/reassistRequests/components/StateBlocks";
import { focusRingSx, TEXT } from "@/features/reassistRequests/utils/tokens";
import { startDownload } from "../utils/download";
import { SaleEmptyState } from "./SaleEmptyState";
import { checkboxSx, SaleFileRow } from "./SaleFileRow";
import { SaleInfoList } from "./SaleInfoList";
import { SaleBuildingTypeChip } from "./SaleBuildingTypeChip";
import { SalePurchaseChip } from "./SalePurchaseChip";
import { SalePurchaseTerms } from "./SalePurchaseTerms";

interface Props {
  documentId: number | null;
  onClose: () => void;
}

/** 失敗した操作の種類。文言が変わる。 */
type Operation = "purchase" | "download";

/**
 * 購入・ダウンロードで失敗したときの、利用者向けの文言。
 * @param error - 例外
 * @param operation - 失敗した操作
 * @returns 文言
 */
const errorMessage = (error: unknown, operation: Operation): string => {
  if (isAxiosError(error)) {
    switch (error.response?.status) {
      case 403:
        return "現在のご契約では、この操作はできません。";
      case 404:
      case 409:
        return operation === "purchase"
          ? "この資料は現在購入できません。一覧を開き直してください。"
          : "この資料は現在ダウンロードできません。";
      default:
        break;
    }
  }
  return operation === "purchase"
    ? "購入できませんでした。少し待ってから、もう一度お試しください。"
    : "ダウンロードできませんでした。少し待ってから、もう一度お試しください。";
};

/** SP のダイアログ。左右 16px の余白を残した縦長のシートにし、文字をページ本体と揃える。 */
const paperSx = (isMobile: boolean): SxProps<Theme> => [
  isMobile
    ? {
        m: 2,
        width: (theme) => `calc(100% - ${theme.spacing(4)})`,
        maxHeight: (theme) => `calc(100% - ${theme.spacing(4)})`,
      }
    : {},
  ...(isMobile ? [listCompactSx as object] : []),
];

/** 動きを減らす設定のとき、ダイアログの出入りの transition を止める。 */
const dialogMotionSx = {
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiBackdrop-root, & .MuiDialog-paper": { transition: "none" },
  },
} as const;

const REDUCE_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const dialogPx = { xs: 2, [MOBILE_BREAKPOINT]: 3 };

/** まとめて購入した結果。1PDFごとの購入APIを順に呼んで集計する。 */
interface PurchaseOutcome {
  succeededIds: number[];
  failedIds: number[];
  /** 購入済みだったため、クレジットを消費しなかった件数。 */
  unchargedCount: number;
  firstError: unknown;
}

/**
 * まとめて購入した結果の案内。
 * @param outcome - 結果
 * @returns 種類と文言。全件失敗・一部失敗は error
 */
const purchaseResultMessage = (
  outcome: PurchaseOutcome
): { kind: "success" | "error"; message: string } => {
  const total = outcome.succeededIds.length + outcome.failedIds.length;
  if (outcome.failedIds.length > 0) {
    // 1件だけのときは、失敗の理由をそのまま伝える
    if (total === 1) {
      return {
        kind: "error",
        message: errorMessage(outcome.firstError, "purchase"),
      };
    }
    return {
      kind: "error",
      message: `${total}件中${
        outcome.failedIds.length
      }件を購入できませんでした。${errorMessage(
        outcome.firstError,
        "purchase"
      )}`,
    };
  }
  if (total === 1) {
    return {
      kind: "success",
      message:
        outcome.unchargedCount === 0
          ? "購入しました。ダウンロードできます。"
          : "購入済みのため、クレジットは消費していません。ダウンロードできます。",
    };
  }
  return {
    kind: "success",
    message:
      outcome.unchargedCount === 0
        ? `${total}件を購入しました。`
        : `${total}件を購入しました。うち${outcome.unchargedCount}件は購入済みのため、クレジットは消費していません。`,
  };
};

/**
 * 購入前の物件詳細。購入できるPDFを一覧し、未購入のPDFを複数選んでまとめて購入する。購入済みのPDFはダウンロードできる。
 */
export const SaleDetailDialog: React.FC<Props> = ({ documentId, onClose }) => {
  const isReadOnly = useReadOnlyUser();
  const isMobile = useIsMobile();
  const { data, isLoading, error, mutate } = useSaleDocumentDetail(documentId);
  const { purchase, fetchDownload } = useSaleActions();
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justPurchasedId, setJustPurchasedId] = useState<number | null>(null);
  const selectAllId = useId();
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCE_MOTION_QUERY).matches;

  // 別の物件を開いたら、選択を持ち越さない
  useEffect(() => {
    setSelectedIds(new Set());
    setConfirmOpen(false);
    setJustPurchasedId(null);
  }, [documentId]);

  // 再取得に失敗したときは、前回の詳細（価格・購入できるPDF）で購入させない
  const detail = data !== undefined && error === undefined ? data : undefined;
  const price = detail?.price ?? null;
  /** 選べるPDF（未購入で、価格が決まっているもの）。 */
  const purchasableFiles =
    detail !== undefined && price !== null
      ? detail.files.filter((file) => !file.isPurchased)
      : [];
  // 購入済みに変わったPDFは、選択から外して数える
  const selectedFiles = purchasableFiles.filter((file) =>
    selectedIds.has(file.id)
  );
  const selectedCount = selectedFiles.length;
  const totalPrice = price !== null ? price * selectedCount : null;
  const allSelected =
    purchasableFiles.length > 0 && selectedCount === purchasableFiles.length;

  const toggleFile = (fileId: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const toggleAll = (): void => {
    setSelectedIds(
      allSelected ? new Set() : new Set(purchasableFiles.map((file) => file.id))
    );
  };

  const handleConfirmPurchase = async (): Promise<void> => {
    if (detail === undefined || selectedFiles.length === 0) return;
    setBusy(true);
    const outcome: PurchaseOutcome = {
      succeededIds: [],
      failedIds: [],
      unchargedCount: 0,
      firstError: undefined,
    };
    // 購入APIは1PDFごと。二重購入を避けるため、並列にせず順に呼ぶ
    for (const file of selectedFiles) {
      try {
        const result = await purchase(detail.id, file.id);
        outcome.succeededIds.push(file.id);
        if (!result.charged) outcome.unchargedCount += 1;
      } catch (e) {
        outcome.failedIds.push(file.id);
        if (outcome.firstError === undefined) outcome.firstError = e;
      }
    }
    const result = purchaseResultMessage(outcome);
    if (result.kind === "success") toast.success(result.message);
    else toast.error(result.message);
    // 購入できなかった行は、未購入のまま選択に戻す（もう一度購入できるように）
    setSelectedIds(new Set(outcome.failedIds));
    setConfirmOpen(false);
    if (outcome.succeededIds.length > 0) {
      setJustPurchasedId(outcome.succeededIds[0]);
      // 購入は完了している。表示の更新だけが失敗しても、「購入できなかった」とは伝えない（再購入を誘わない）
      // SWR の mutate() は、再取得に失敗しても例外にせず、結果（詳細）を返さない（undefined）。それで失敗を判定する
      let refreshed = false;
      try {
        refreshed = (await mutate()) !== undefined;
      } catch {
        refreshed = false;
      }
      if (!refreshed) {
        toast.warning(
          "購入は完了しています。表示を更新できなかったため、ダイアログを開き直してください。"
        );
      }
    }
    setBusy(false);
  };

  const handleDownload = async (file: SaleFile): Promise<void> => {
    if (data === undefined) return;
    setBusy(true);
    try {
      const result = await fetchDownload(data.id, file.id);
      startDownload(result.url, result.fileName);
    } catch (e) {
      toast.error(errorMessage(e, "download"));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (): void => {
    setJustPurchasedId(null);
    setSelectedIds(new Set());
    onClose();
  };

  return (
    <>
      <Dialog
        open={documentId !== null}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="sale-detail-title"
        transitionDuration={reduceMotion ? 0 : undefined}
        PaperProps={{ sx: paperSx(isMobile) }}
        sx={dialogMotionSx}
      >
        <DialogTitle
          id="sale-detail-title"
          component="h2"
          sx={{
            px: dialogPx,
            pr: 7,
            pt: 2.5,
            pb: data !== undefined ? 0 : undefined,
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.4,
            overflowWrap: "anywhere",
          }}
        >
          {data !== undefined ? displayName(data) : "資料の詳細"}
        </DialogTitle>
        <IconButton
          aria-label="閉じる"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            top: 8,
            right: 8,
            width: MIN_TAP_TARGET,
            height: MIN_TAP_TARGET,
            ...focusRingSx(theme),
          })}
        >
          <CloseOutlined />
        </IconButton>
        {data !== undefined && (
          <Box
            sx={{
              px: dialogPx,
              pt: 1,
              pb: 1,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <SalePurchaseChip
              fileCount={data.fileCount}
              purchasedCount={data.purchasedCount}
            />
            <SaleBuildingTypeChip buildingType={data.buildingType} />
          </Box>
        )}
        <DialogContent sx={{ px: dialogPx }}>
          {isLoading && (
            <Box
              role="status"
              sx={(theme) => ({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: theme.spacing(20),
              })}
            >
              <CircularProgress aria-label="読み込み中" />
            </Box>
          )}
          {error !== undefined && (
            <LoadFailed
              message="資料を読み込めませんでした。販売が終了している可能性があります。"
              onRetry={() => {
                mutate().catch(() => undefined);
              }}
            />
          )}
          {/* 再取得に失敗したときは、前回の詳細（価格・購入できるPDF）を出さない。確認できていない内容で購入させないため */}
          {detail !== undefined && (
            <Stack gap={{ xs: 2, [MOBILE_BREAKPOINT]: 2.5 }}>
              <Stack gap={1}>
                <Typography component="h3" sx={TEXT.cardTitle}>
                  所在情報
                </Typography>
                <SaleInfoList
                  items={[
                    { label: "住所", value: detail.address },
                    { label: "地番", value: detail.chiban },
                    { label: "家屋番号", value: detail.kaokuNumber },
                    // 登記と突き合わせて物件を見分けるための項目。値があるものだけを出す（建物の種類はタイトル下のタグで出す）
                    ...[
                      { label: "階数", value: detail.floors },
                      { label: "構造", value: detail.structure },
                      { label: "用途", value: detail.use },
                    ].filter((item) => item.value.trim() !== ""),
                  ]}
                />
              </Stack>
              <Stack gap={1}>
                <Typography component="h3" sx={TEXT.cardTitle}>
                  アーカイブ提供資料として購入できるPDF
                </Typography>
                {price !== null && (
                  <Typography
                    sx={{
                      ...TEXT.body,
                      color: "text.secondary",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {`購入すると、1PDFにつき${formatPriceWithYen(
                      price
                    )}を消費します。`}
                  </Typography>
                )}
                {detail.files.length === 0 ? (
                  <SaleEmptyState
                    compact
                    icon={SearchOffOutlined}
                    title="購入できるPDFはありません"
                  />
                ) : (
                  <>
                    {purchasableFiles.length > 0 && (
                      <Box
                        component="label"
                        htmlFor={selectAllId}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          columnGap: 1,
                          alignSelf: "flex-start",
                          pl: 0.5,
                          pr: 1,
                          cursor: busy ? "default" : "pointer",
                        }}
                      >
                        <Checkbox
                          id={selectAllId}
                          checked={allSelected}
                          indeterminate={selectedCount > 0 && !allSelected}
                          disabled={busy}
                          onChange={toggleAll}
                          sx={checkboxSx}
                        />
                        <Typography sx={{ ...TEXT.body, fontWeight: 700 }}>
                          未購入をすべて選ぶ
                        </Typography>
                      </Box>
                    )}
                    <Box
                      component="ul"
                      sx={{ p: 0, m: 0, listStyle: "none" }}
                      aria-label="購入できるPDF"
                    >
                      {detail.files.map((file) => (
                        <SaleFileRow
                          key={file.id}
                          documentType={file.documentType}
                          pageCount={file.pageCount}
                          isPurchased={file.isPurchased}
                          priceUnset={price === null}
                          busy={busy}
                          selected={
                            !file.isPurchased && selectedIds.has(file.id)
                          }
                          autoFocusAction={justPurchasedId === file.id}
                          reserveCheckboxSpace={detail.files.some(
                            (f) => !f.isPurchased
                          )}
                          onToggle={() => {
                            toggleFile(file.id);
                          }}
                          onDownload={() => {
                            handleDownload(file).catch(() => undefined);
                          }}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Stack>
              {purchasableFiles.length > 0 && <SalePurchaseTerms />}
            </Stack>
          )}
        </DialogContent>
        {/* 購入バー。DialogContent の外に置き、一覧をスクロールしても下に残す */}
        <DialogActions
          sx={(theme) => ({
            px: dialogPx,
            pt: purchasableFiles.length > 0 ? 1.5 : 1,
            pb: 2,
            gap: 1.5,
            flexWrap: "wrap",
            "& > :not(:first-of-type)": { ml: 0 },
            ...(purchasableFiles.length > 0
              ? {
                  backgroundColor: theme.palette.white.main,
                  borderTop: `1px solid ${theme.palette.border.main}`,
                }
              : {}),
          })}
        >
          {purchasableFiles.length > 0 && (
            <Typography
              role="status"
              aria-live="polite"
              sx={{
                ...TEXT.body,
                flex: { xs: "1 1 100%", [MOBILE_BREAKPOINT]: "1 1 auto" },
                minWidth: 0,
                color: selectedCount > 0 ? "text.primary" : "text.secondary",
                fontWeight: selectedCount > 0 ? 700 : 400,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {selectedCount > 0 && totalPrice !== null
                ? `${selectedCount}件を選択中・合計 ${formatPriceWithYen(
                    totalPrice
                  )}`
                : "購入するPDFを選んでください"}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              ml: "auto",
              flex: {
                xs: purchasableFiles.length > 0 ? "1 1 100%" : "0 0 auto",
                [MOBILE_BREAKPOINT]: "0 0 auto",
              },
              "& .MuiButton-root": {
                flex: {
                  xs: purchasableFiles.length > 0 ? 1 : "0 0 auto",
                  [MOBILE_BREAKPOINT]: "0 0 auto",
                },
              },
            }}
          >
            <ReassistButton
              label="閉じる"
              variant="text"
              onClick={handleClose}
            />
            {purchasableFiles.length > 0 && (
              <ReadOnlyTooltip isReadOnly={isReadOnly}>
                <Button
                  variant="contained"
                  className="TapExpanded"
                  disableElevation
                  disabled={busy || selectedCount === 0 || isReadOnly}
                  onClick={() => {
                    setConfirmOpen(true);
                  }}
                  sx={(theme) => ({
                    ...reassistButtonSx("contained", "page")(theme),
                    minWidth: theme.spacing(16),
                  })}
                >
                  {selectedCount > 0
                    ? `同意して${selectedCount}件を購入する`
                    : "同意して購入する"}
                </Button>
              </ReadOnlyTooltip>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!busy) setConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="sale-confirm-title"
        aria-describedby="sale-confirm-body"
        transitionDuration={reduceMotion ? 0 : undefined}
        PaperProps={{ sx: paperSx(isMobile) }}
        sx={dialogMotionSx}
      >
        <DialogTitle
          id="sale-confirm-title"
          component="h2"
          sx={{ px: dialogPx, fontSize: 20, fontWeight: 700, lineHeight: 1.4 }}
        >
          {selectedCount > 1
            ? `${selectedCount}件のPDFを購入しますか？`
            : "このPDFを購入しますか？"}
        </DialogTitle>
        <DialogContent
          id="sale-confirm-body"
          aria-busy={busy}
          sx={{ px: dialogPx }}
        >
          {detail !== undefined && selectedCount > 0 && (
            <SaleInfoList
              items={[
                { label: "物件", value: displayName(detail) },
                {
                  label: "資料",
                  value: (
                    <Box
                      component="ul"
                      aria-label="購入する資料"
                      sx={{ m: 0, p: 0, listStyle: "none" }}
                    >
                      {selectedFiles.map((file) => (
                        <li key={file.id}>{formatFileLabel(file)}</li>
                      ))}
                    </Box>
                  ),
                },
                {
                  label:
                    selectedCount > 1
                      ? "消費するクレジット（合計）"
                      : "消費するクレジット",
                  value: formatPriceWithYen(totalPrice),
                  emphasis: true,
                },
              ]}
            />
          )}
          <Typography sx={{ ...TEXT.body, mt: 2, color: "text.secondary" }}>
            購入後、すぐにダウンロードできます。
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: dialogPx,
            pb: 2,
            gap: 1.5,
            "& > :not(:first-of-type)": { ml: 0 },
            "& .MuiButton-root": {
              minWidth: (theme) => theme.spacing(14),
              flex: { xs: 1, [MOBILE_BREAKPOINT]: "0 0 auto" },
            },
          }}
        >
          <Button
            autoFocus
            className="TapExpanded"
            disabled={busy}
            onClick={() => {
              setConfirmOpen(false);
            }}
            sx={reassistButtonSx("text", "page")}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            className="TapExpanded"
            disableElevation
            disabled={busy}
            startIcon={
              busy ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            onClick={() => {
              handleConfirmPurchase().catch(() => undefined);
            }}
            sx={reassistButtonSx("contained", "page")}
          >
            購入する
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
